<script lang="ts">
	/**
	 * Terms a buyer must tick before purchasing (Design §4.7).
	 *
	 * Authored beside the catalogue because a term belongs to products and families, and the person
	 * writing one is already looking at both.
	 *
	 * A term applies to **as many** of them as it needs to: an export restriction covering three
	 * product lines is one set of words, not three copies that will drift apart.
	 *
	 * Two things this screen has to say, because both are counter-intuitive and both matter:
	 *
	 * **Editing the wording creates a new version.** Buyers who already accepted are unaffected —
	 * their acceptance recorded the words they saw — but anyone mid-checkout is asked to read the
	 * new text rather than having their tick applied to words they never saw. Renaming the label
	 * does not do that, because it changes nothing anybody agreed to.
	 *
	 * **A term is retired, never deleted.** Every acceptance references it, and a term that
	 * vanishes takes the evidence of every agreement to it along with it.
	 */

	import { createEventDispatcher } from 'svelte';
	import { Badge, Button, Checkbox, Input, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell, Textarea, Toggle } from 'flowbite-svelte';
	import { ConfirmButton, EmptyState, FormField, FormModal, Panel } from '../../../components';
	import { createClickwrap, deleteClickwrap, updateClickwrap } from '../../../AppNetwork';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import type { AdminClickwrap, Product, ProductFamily } from '../../../AppTypes';

	export let clickwraps: AdminClickwrap[] = [];
	export let products: Product[] = [];
	export let families: ProductFamily[] = [];
	export let loading: boolean = false;

	const dispatcher = createEventDispatcher();

	let isAdding: boolean = false;
	let saving: boolean = false;
	let editingUUID: string = '';

	function emptyDraft() {
		return {
			productUUIDs: [] as string[],
			familySlugs: [] as string[],
			title: '',
			bodyHTML: '',
			displayOrder: 0,
			// Blank means the term asks nothing, which is the common case. Held as a string rather
			// than `string | null` so the same `<Input>` binds to both forms, and normalised to
			// null on submit — `''` and null would be two spellings of "asks nothing" and every
			// gate, here and on the server, tests the column for presence.
			inputLabel: ''
		};
	}

	let draft = emptyDraft();
	let edit = {
		title: '',
		bodyHTML: '',
		displayOrder: 0,
		isActive: true,
		inputLabel: '',
		productUUIDs: [] as string[],
		familySlugs: [] as string[]
	};

	/** Blank is "no question", which the server stores as null. See `emptyDraft`. */
	function labelOrNull(value: string): string | null {
		return value.trim().length === 0 ? null : value.trim();
	}

	/** Toggling one target, kept out of the markup so the lists stay readable. */
	function fnToggle(list: string[], value: string, event: Event): string[] {
		return (event.target as HTMLInputElement).checked
			? [...list, value]
			: list.filter(entry => entry !== value);
	}

	$: editing = clickwraps.find(term => term.uuid === editingUUID) ?? null;
	// A term that applies to nothing is never shown to anybody, so neither form can save one.
	$: canCreate =
		!saving &&
		draft.title.trim().length > 0 &&
		draft.bodyHTML.trim().length > 0 &&
		draft.productUUIDs.length + draft.familySlugs.length > 0;
	$: canSaveEdit =
		!saving &&
		edit.title.trim().length > 0 &&
		edit.bodyHTML.trim().length > 0 &&
		edit.productUUIDs.length + edit.familySlugs.length > 0;
	/**
	 * Whether Save would bump the version, so the consequence is visible before it happens.
	 *
	 * Two edits do it, and they are one rule twice: the version exists so a page already on screen
	 * cannot produce an acceptance it should not, and both new wording and a newly-added question
	 * are changes a rendered page cannot honour — the second because that page has no field to
	 * answer with, and a buyer refused over a box they were never shown reads as a broken store.
	 * Relabelling a question does not, since the answer is snapshotted with the wording it
	 * answered; nor does taking one away, which a rendered page still satisfies.
	 */
	$: wordingChanged = editing !== null && edit.bodyHTML !== editing.bodyHTML;
	$: questionAdded =
		editing !== null && labelOrNull(edit.inputLabel) !== null && (editing.inputLabel ?? null) === null;
	$: versionBumps = wordingChanged || questionAdded;

	function fnStartEdit(term: AdminClickwrap): void {
		editingUUID = term.uuid;
		edit = {
			title: term.title,
			bodyHTML: term.bodyHTML,
			displayOrder: term.displayOrder,
			isActive: term.isActive,
			inputLabel: term.inputLabel ?? '',
			productUUIDs: [...term.productUUIDs],
			familySlugs: [...term.familySlugs]
		};
	}

	async function fnCreate(): Promise<void> {
		saving = true;
		try {
			await createClickwrap({
				// Both lists, always: the server refuses a term that applies to nothing, and
				// sending them together is what lets one term cover a line and a plan at once.
				productUUIDs: draft.productUUIDs,
				familySlugs: draft.familySlugs,
				title: draft.title.trim(),
				bodyHTML: draft.bodyHTML.trim(),
				displayOrder: Number(draft.displayOrder),
				inputLabel: labelOrNull(draft.inputLabel)
			});
			toastSuccess(`${draft.title.trim()} added.`);
			draft = emptyDraft();
			isAdding = false;
			dispatcher('changed');
		} catch (error) {
			toastError('The term was not added.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	/**
	 * Deletes a term, which always takes it off every product and family it named.
	 *
	 * The row itself only goes when nobody accepted it. An acceptance is the evidence a customer
	 * agreed and it names the term, so an accepted one is retired instead — and the toast says
	 * which happened, because a term still in the list after Delete otherwise reads as a failure.
	 */
	async function fnDelete(term: AdminClickwrap): Promise<void> {
		saving = true;
		try {
			const result = await deleteClickwrap(term.uuid);
			if (result.deleted) {
				toastSuccess(`${term.title} deleted.`);
			} else {
				toastSuccess(
					`${term.title} retired.`,
					`${result.acceptanceCount} acceptance${result.acceptanceCount === 1 ? '' : 's'} name it, so the record is kept. It applies to nothing and nobody new is asked.`
				);
			}
			if (editingUUID === term.uuid) {
				editingUUID = '';
			}
			dispatcher('changed');
		} catch (error) {
			toastError('The term was not deleted.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	async function fnSaveEdit(): Promise<void> {
		if (editing === null) {
			return;
		}
		saving = true;
		try {
			await updateClickwrap(editing.uuid, {
				title: edit.title.trim(),
				bodyHTML: edit.bodyHTML.trim(),
				displayOrder: Number(edit.displayOrder),
				isActive: edit.isActive,
				// Always sent, so clearing the box actually stops the term asking. Omitting it
				// would leave the question in place while the form showed it gone.
				inputLabel: labelOrNull(edit.inputLabel),
				// Sent together, because either list alone **replaces** the scope on the server.
				productUUIDs: edit.productUUIDs,
				familySlugs: edit.familySlugs
			});
			toastSuccess(`${edit.title.trim()} saved.`);
			editingUUID = '';
			dispatcher('changed');
		} catch (error) {
			toastError('The term was not saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}
</script>

<Panel title="Purchase terms" faIcon="file-signature">
	<svelte:fragment slot="titleRight">
		<Button size="sm" color="primary" on:click={() => (isAdding = true)}>
			<i class="fa-solid fa-plus mr-2" />Add a term
		</Button>
	</svelte:fragment>

	<p class="text-sm text-default mb-4">
		<i class="fa-solid fa-circle-info mr-2" />Checkboxes the buyer must tick before the purchase
		can go through. A family term covers every product in the line; a product term covers one
		plan, and a buyer owes both. The gate is enforced when the checkout is created, so it holds
		whatever the storefront does.
	</p>

	{#if loading}
		<EmptyState variant="loading" title="Loading terms…" />
	{:else if clickwraps.length === 0}
		<EmptyState
			title="No purchase terms."
			description="Nothing has to be accepted before buying. Add a term to require it: every purchase from then on records who accepted what, and the words they were shown."
		>
			<Button color="primary" size="sm" on:click={() => (isAdding = true)}>Add the first term</Button>
		</EmptyState>
	{:else}
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Term</TableHeadCell>
				<TableHeadCell>Applies to</TableHeadCell>
				<TableHeadCell>Version</TableHeadCell>
				<TableHeadCell>Accepted</TableHeadCell>
				<TableHeadCell>State</TableHeadCell>
				<TableHeadCell />
			</TableHead>
			<TableBody>
				{#each clickwraps as term (term.uuid)}
					<TableBodyRow>
						<TableBodyCell class="dataTable font-medium">
							{term.title}
							{#if (term.inputLabel ?? '').trim().length > 0}
								<!--
									Said on the list, not only in the form: a term that asks a
									question puts a mandatory field in front of every buyer, and an
									admin scanning the terms has no other way to see which ones do.
								-->
								<span class="block text-xs font-normal text-default">
									<i class="fa-solid fa-keyboard mr-1" />Asks for {term.inputLabel}
								</span>
							{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<!--
								Every target listed, not a count. "3 things" is a number nobody can
								act on; the question an admin asks here is which line a term covers.
							-->
							<div class="flex flex-wrap gap-1">
								{#each term.familySlugs as slug (slug)}
									<Badge color="blue">{families.find(family => family.slug === slug)?.name ?? slug}</Badge>
								{/each}
								{#each term.productUUIDs as uuid (uuid)}
									<Badge color="purple">
										{products.find(product => product.uuid === uuid)?.name ?? uuid}
									</Badge>
								{/each}
								{#if term.familySlugs.length + term.productUUIDs.length === 0}
									<span class="text-default text-xs">Nothing</span>
								{/if}
							</div>
						</TableBodyCell>
						<TableBodyCell class="dataTable">v{term.version}</TableBodyCell>
						<TableBodyCell class="dataTable">{term.acceptanceCount}</TableBodyCell>
						<TableBodyCell class="dataTable">
							{#if term.isActive}
								<Badge color="green">Required</Badge>
							{:else}
								<!-- Retired, not deleted: the acceptances stand, and nobody new is asked. -->
								<Badge color="dark">Retired</Badge>
							{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<div class="flex justify-end gap-2">
								<Button size="xs" color="alternative" on:click={() => fnStartEdit(term)}>
									<i class="fa-solid fa-pen mr-1" />Edit
								</Button>
								<!--
									Two-step, like every other destructive control: this is the one
									action here that can take a term off a live product, and the
									purchases made from that moment on record no agreement at all.
								-->
								<ConfirmButton
									size="xs"
									color="red"
									disabled={saving}
									confirmText={term.acceptanceCount > 0
										? `${term.acceptanceCount} customer${term.acceptanceCount === 1 ? ' has' : 's have'} accepted “${term.title}”, so the record is kept and it is retired instead. Take it off every product and family?`
										: `Delete “${term.title}”? Nobody has accepted it, so it goes for good.`}
									on:confirm={() => fnDelete(term)}
								>
									<i class="fa-solid fa-trash mr-1" />Delete
								</ConfirmButton>
							</div>
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>
	{/if}

	<FormModal
		bind:open={isAdding}
		title="New purchase term"
		faIcon="file-signature"
		submitLabel="Add term"
		size="lg"
		busy={saving}
		canSubmit={canCreate}
		description="Shown as a checkbox at checkout. The buyer cannot continue until every term is ticked."
		on:submit={fnCreate}
	>
		<div class="grid md:grid-cols-2 gap-4">
			<!--
				A plain flow list, and the same `spacing="" class="shrink-0"` every other one of
				these carries. Both forms here had neither, and the two defects compounded: the
				lists sat in a `max-h-40 overflow-y-auto` **inside a dialog body that already
				scrolls**, so everything past the sixth option was cut off behind a nested scroll
				region nothing marked as one — and the thin scrollbar the design system draws gave no
				hint there was more to see. `overflow-y: auto` also computes `overflow-x` to `auto`
				alongside it, which clipped the focus ring off the checkbox at the container's edge.
				Without `shrink-0` the box is a flex item beside a long product name and can be
				squeezed narrower than its own square, and flowbite's default `me-2` on top of the
				label's `gap-2` set these apart from every comparable list in the app.
			-->
			<FormField
				label="Families it applies to"
				description="A product family covers every product in it, which is where terms usually live."
			>
				<div class="space-y-1">
					{#each families as family (family.slug)}
						<label class="flex items-center gap-2 text-sm">
							<Checkbox
								checked={draft.familySlugs.includes(family.slug)}
								disabled={saving}
								spacing=""
								class="shrink-0"
								on:change={event =>
									(draft.familySlugs = fnToggle(draft.familySlugs, family.slug, event))}
							/>
							{family.name}
						</label>
					{/each}
				</div>
			</FormField>

			<FormField
				label="Products it applies to"
				description="For terms that cover one product rather than a whole product family. A buyer owes both."
			>
				<div class="space-y-1">
					{#each products as product (product.uuid)}
						<label class="flex items-center gap-2 text-sm">
							<Checkbox
								checked={draft.productUUIDs.includes(product.uuid)}
								disabled={saving}
								spacing=""
								class="shrink-0"
								on:change={event =>
									(draft.productUUIDs = fnToggle(draft.productUUIDs, product.uuid, event))}
							/>
							{product.name}
						</label>
					{/each}
				</div>
			</FormField>

			<FormField id="clickwrap-title" label="Label" required description="The words beside the checkbox.">
				<Input id="clickwrap-title" bind:value={draft.title} placeholder="Terms of Service" disabled={saving} />
			</FormField>

			<FormField id="clickwrap-order" label="Order" description="Lower appears first, so a reading order can be stated.">
				<Input id="clickwrap-order" type="number" min="0" step="1" bind:value={draft.displayOrder} disabled={saving} />
			</FormField>

			<!--
				A term asks a question or it does not, and the caption **is** the switch — a
				checkbox beside a label would be two spellings of one fact, and an enabled input
				with no caption is a box asking for something unnamed.
			-->
			<FormField
				id="clickwrap-input"
				label="Also ask for"
				description="Leave blank to ask nothing. Filled in, the buyer gets a text box beside the tick and cannot buy without answering — and the answer is recorded with the acceptance."
			>
				<Input id="clickwrap-input" bind:value={draft.inputLabel} placeholder="Project name" maxlength={120} disabled={saving} />
			</FormField>
		</div>

		<FormField
			id="clickwrap-body"
			label="What they are agreeing to"
			required
			description="HTML. Rendered inside `.richtext`, so paragraphs and lists survive."
		>
			<Textarea id="clickwrap-body" rows="8" bind:value={draft.bodyHTML} disabled={saving} />
		</FormField>
	</FormModal>

	{#if editing !== null}
		<FormModal
			open={true}
			title={`Edit ${editing.title}`}
			faIcon="pen"
			submitLabel="Save term"
			size="lg"
			busy={saving}
			canSubmit={canSaveEdit}
			description="Customers who already accepted keep the version they agreed to, because their acceptance recorded the words they were shown."
			on:submit={fnSaveEdit}
			on:cancel={() => (editingUUID = '')}
		>
			<div class="grid md:grid-cols-2 gap-4">
				<FormField
					label="Families it applies to"
					description="Adding one asks every future buyer in that line to accept it. Purchases already made are unaffected."
				>
					<div class="space-y-1">
						{#each families as family (family.slug)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.familySlugs.includes(family.slug)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(edit.familySlugs = fnToggle(edit.familySlugs, family.slug, event))}
								/>
								{family.name}
							</label>
						{/each}
					</div>
				</FormField>

				<FormField label="Products it applies to">
					<div class="space-y-1">
						{#each products as product (product.uuid)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.productUUIDs.includes(product.uuid)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(edit.productUUIDs = fnToggle(edit.productUUIDs, product.uuid, event))}
								/>
								{product.name}
							</label>
						{/each}
					</div>
				</FormField>

				<FormField id="clickwrap-edit-title" label="Label" required>
					<Input id="clickwrap-edit-title" bind:value={edit.title} disabled={saving} />
				</FormField>

				<FormField id="clickwrap-edit-order" label="Order">
					<Input
						id="clickwrap-edit-order"
						type="number"
						min="0"
						step="1"
						bind:value={edit.displayOrder}
						disabled={saving}
					/>
				</FormField>

				<FormField
					id="clickwrap-edit-input"
					label="Also ask for"
					description="Blank asks nothing. Everyone who already accepted keeps the question they were asked and the answer they gave, whatever this says afterwards."
				>
					<Input id="clickwrap-edit-input" bind:value={edit.inputLabel} placeholder="Project name" maxlength={120} disabled={saving} />
				</FormField>
			</div>

			<FormField
				id="clickwrap-edit-body"
				label="What they are agreeing to"
				required
				description="HTML. Rendered inside `.richtext`."
			>
				<Textarea id="clickwrap-edit-body" rows="8" bind:value={edit.bodyHTML} disabled={saving} />
			</FormField>

			{#if versionBumps}
				<!--
					Said before Save rather than after. An admin correcting a typo should know that
					anyone mid-checkout will be asked to read it again — and that everyone who
					already accepted keeps the version they agreed to.

					A newly-added question does the same and for a sharper reason: a page already on
					screen has no box to answer it with, so without the bump those buyers would be
					refused over a field they were never shown.
				-->
				<p class="text-sm text-amber-700 dark:text-amber-400">
					<i class="fa-solid fa-triangle-exclamation mr-2" />
					{wordingChanged ? 'The wording changed' : 'This term now asks a question'}, so it
					becomes <strong>v{editing.version + 1}</strong>. Buyers part-way through checkout
					will be asked to read it again; the {editing.acceptanceCount} who already accepted keep
					the version they agreed to.
				</p>
			{/if}

			<div class="flex items-center gap-3">
				<Toggle id="clickwrap-edit-active" bind:checked={edit.isActive} disabled={saving} />
				<label for="clickwrap-edit-active" class="text-sm">
					Required at checkout. Turning it off stops asking anyone new. It does not withdraw
					it from the {editing.acceptanceCount} who already accepted.
				</label>
			</div>
		</FormModal>
	{/if}
</Panel>
